// Shared locale-aware formatting helpers.
//
// Every date/time/number/currency shown to the user must follow the language
// the user selected in the app (the vue-i18n locale), NOT the browser's OS
// locale and NOT a hardcoded 'pt-BR'. These helpers read the app locale from
// the i18n singleton, so a value formatted inside a component's render (a
// computed or a template call) re-renders automatically when the language
// switcher changes the locale.
//
// We intentionally do NOT force `hour12`/24h or a fixed date order: passing the
// locale lets Intl pick the right convention per language (pt-BR -> dd/mm/yyyy,
// 24h; en-US -> mm/dd/yyyy, AM/PM). Callers pass Intl options for granularity.

import i18n from '@/plugins/i18n';

// The app persists/uses 'pt-BR' or 'en-US', but a few call sites set the bare
// 'en'. Normalize any variant to a full BCP-47 tag Intl understands.
export const resolveLocale = (): string => {
  const raw = i18n.global.locale;
  const value = typeof raw === 'string' ? raw : raw.value;
  if (value?.toLowerCase().startsWith('pt')) return 'pt-BR';
  if (value?.toLowerCase().startsWith('en')) return 'en-US';
  return value || 'pt-BR';
};

type DateInput = Date | string | number | null | undefined;

const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

// Date only, e.g. pt-BR "09/07/2026" / en-US "07/09/2026".
export const formatDate = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
): string => {
  const d = toDate(value);
  return d ? d.toLocaleDateString(resolveLocale(), options) : '';
};

// Time only, e.g. pt-BR "23:03" / en-US "11:03 PM".
export const formatTime = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_TIME_OPTIONS,
): string => {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(resolveLocale(), options) : '';
};

// Date + time, e.g. pt-BR "09/07/2026, 23:03:54" / en-US "7/9/2026, 11:03:54 PM".
export const formatDateTime = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { ...DEFAULT_DATE_OPTIONS, ...DEFAULT_TIME_OPTIONS },
): string => {
  const d = toDate(value);
  return d ? d.toLocaleString(resolveLocale(), options) : '';
};

// Plain number with locale grouping/decimal separators.
export const formatNumber = (
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string => new Intl.NumberFormat(resolveLocale(), options).format(value ?? 0);

// Currency. The currency code travels with the value (default BRL); the locale
// only controls symbol placement and separators.
export const formatCurrency = (
  value: number | null | undefined,
  currency = 'BRL',
  options: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(resolveLocale(), {
  style: 'currency',
  currency,
  ...options,
}).format(value ?? 0);
